import { YellowButton } from '@/components/button/button';
import {
  AttendeeStatus,
  ExperienceFieldsFragment,
  useExperienceEventsQuery,
} from '@/generated/graphql';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import BookingButton from './event-booking-button';
import EventBookingModal from './event-booking-modal';
import { has } from 'lodash';

const Times = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 136.8%;
  color: #251628;
`;
const TimesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: auto;
  width: 100%;
  padding: 2px 0;
  align-items: center;
  justify-content: center;
  column-gap: 10px;
  @media (max-width: 768px) {
    display: none;
  }
`;
const MoreTimesLink = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 159.3%;
  text-decoration-line: underline;
  color: #7a3b6e;
  cursor: pointer;
`;

type EventActionsProps = {
  experience: ExperienceFieldsFragment;
};
const EventActions = ({ experience }: EventActionsProps): React.ReactElement => {
  const now = useMemo(() => DateTime.now().startOf('hour').toISO(), []);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { data, loading } = useExperienceEventsQuery({
    variables: {
      id: experience?.id,
      now,
    },
    skip: !experience?.id,
  });
  const events = data?.events?.edges || [];
  const nextEvent = events[0] ? events[0].node : null;
  const hasMoreEvents = events.length > 1;
  if (!nextEvent?.start || !nextEvent?.end) {
    return <></>;
  }

  const start = DateTime.fromISO(nextEvent?.start).toLocaleString({
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const end = DateTime.fromISO(nextEvent?.end).toLocaleString({
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  if (loading || !experience?.id || !events || !events.length) {
    return <></>;
  }

  const capacityLimitDefined =
    experience?.maximumNumberOfParticipantsPerEvent &&
    experience?.maximumNumberOfParticipantsPerEvent > 0;

  let hasSpotsRemaining = true;
  let spotsLeft = 0;
  if (capacityLimitDefined && has(nextEvent, 'attendees.edges')) {
    const numberOfAttendees =
      nextEvent.attendees?.edges?.filter(
        edge => edge?.node?.role !== 'host' && edge?.node?.status !== AttendeeStatus.Cancelled,
      )?.length || 0;
    spotsLeft = (experience?.maximumNumberOfParticipantsPerEvent || 0) - numberOfAttendees;
    hasSpotsRemaining = spotsLeft > 0;
  }

  return (
    <>
      <TimesWrapper>
        <Times>
          {start}-{end}{' '}
          {capacityLimitDefined && hasSpotsRemaining && (
            <b>
              <br />
              {`(${spotsLeft} spots left)`}
            </b>
          )}
        </Times>
        {hasMoreEvents && (
          <MoreTimesLink onClick={() => setModalOpen(true)}>More Times</MoreTimesLink>
        )}
      </TimesWrapper>
      {nextEvent ? (
        capacityLimitDefined && !hasSpotsRemaining ? (
          <YellowButton disabled>No more spots left</YellowButton>
        ) : (
          <BookingButton event={nextEvent} experience={experience} />
        )
      ) : (
        <YellowButton disabled>Unavailable</YellowButton>
      )}
      <EventBookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        events={events.map(edge => edge.node)}
        capacityLimitDefined={capacityLimitDefined === true}
        maxSpots={experience?.maximumNumberOfParticipantsPerEvent}
      />
    </>
  );
};

export default EventActions;
